import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Parser} from 'htmlparser2'
import {useEditMode} from 'Event/EditModeProvider'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {spacing} from 'lib/ui/theme'
import Button from '@material-ui/core/Button'
import {useAttendeeVariables} from 'Event'
import withStyles from '@material-ui/core/styles/withStyles'
import {BodyHTMLEmbedConfig} from 'Event/Dashboard/components/BodyHtmlEmbed/BodyHTMLEmbedConfig'
import {useToggle} from 'lib/toggle'
import {useTemplate} from 'Event/TemplateProvider'
import ErrorBoundary from 'Event/Dashboard/components/BodyHtmlEmbed/ErrorBoundary'
import {useIsAttendee} from 'Event/auth'

export default function BodyHtmlEmbed() {
  const isEditMode = useEditMode()
  const isAttendee = useIsAttendee()

  if (isEditMode) {
    return <EditButton />
  }

  // Only want to show a script/embed error for users, not attendees.
  const showExecuteError = isAttendee === false

  return (
    // Surround in error boundary to prevent bad scripts
    // from crashing the app.
    <ErrorBoundary showError={showExecuteError}>
      <Body />
    </ErrorBoundary>
  )
}

export function Body() {
  const anchor = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [scriptElements, setScriptElements] = useState<HTMLElement[]>([])
  const template = useTemplate()
  const {bodyHTMLEmbed: bodyEmbed} = template
  const v = useAttendeeVariables()

  const html = bodyEmbed ? v(bodyEmbed) : ''

  const el = useRef<HTMLElement | null>(null)

  /**
   * Parse script elements
   */

  const parser = useMemo(
    () =>
      new Parser({
        onopentag(name, attributes) {
          if (name !== 'script') {
            return
          }

          el.current = document.createElement(name)

          for (const [key, value] of Object.entries(attributes)) {
            el.current.setAttribute(key, value)
          }
        },
        ontext(text) {
          if (el.current) {
            el.current.innerHTML = el.current.innerHTML + text
          }
        },
        onclosetag() {
          const built = el.current
          if (!anchor.current || !built || !isScript(built)) {
            return
          }

          setScriptElements((current) => [...current, built])
          el.current = null
        },
      }),
    [],
  )

  useEffect(() => {
    parser.write(html)
    parser.end()
  }, [parser, html])

  /**
   * Dynamically load, and execute script elements
   */

  useEffect(() => {
    const hasEl = scriptElements.length > 0
    if (!anchor.current || isLoading || !hasEl) {
      return
    }

    const nextEl = scriptElements[0]

    const isExternalScript = isScript(nextEl) && nextEl.src
    if (isExternalScript) {
      setIsLoading(true)
      nextEl.onload = function () {
        setIsLoading(false)
      }
    }

    const removed = scriptElements.filter((_, index) => index !== 0)
    anchor.current.insertBefore(nextEl, null)
    setScriptElements(removed)
  }, [isLoading, scriptElements])

  return (
    <IfLive>
      <div ref={anchor} dangerouslySetInnerHTML={{__html: html}}></div>
    </IfLive>
  )
}

function isScript(element: HTMLElement): element is HTMLScriptElement {
  return element.tagName === 'SCRIPT'
}

function EditButton() {
  const {flag: configVisible, toggle: toggleConfig} = useToggle()

  return (
    <>
      <EditModeOnly>
        <BodyHTMLEmbedConfig showing={configVisible} onClose={toggleConfig} />
        <StyledEditButton
          onClick={toggleConfig}
          fullWidth
          size="large"
          variant="outlined"
          color="primary"
          aria-label="edit body html embed"
        >
          Embed HTML
        </StyledEditButton>
      </EditModeOnly>
    </>
  )
}

const StyledEditButton = withStyles({
  root: {
    marginTop: spacing[10],
    marginBottom: spacing[4],
  },
})(Button)

// We don't want to render / embed scripts in edit mode.
function IfLive(props: {children: JSX.Element}) {
  const isEditMode = useEditMode()
  if (isEditMode) {
    return null
  }

  return props.children
}
